"use client";

import Image from "next/image";
import { useSidebar } from "@/contexts/sidebar-context";
import { useLanguage } from "@/contexts/language-context";
import { Plus } from "lucide-react";
import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/auth-context";
import { SessionAccessDialog } from "@/components/session-access-dialog";
import { useNewChat } from "@/hooks/use-new-chat";

export default function ShowSidebarButton() {
  const { isOpen, openSidebar, closeSidebar } = useSidebar();
  const { t, isRTL, language } = useLanguage();
  const { user } = useAuth();
  const newChat = useNewChat();
  const [hasMounted, setHasMounted] = useState(false);

  const label = isOpen ? t('common.hideSidebar') : t('common.showSidebar');
  const togglePositionClass = isRTL ? (isOpen ? 'right-[236px]' : 'right-[132px]') : isOpen ? 'left-[236px]' : 'left-[132px]';
  const newChatPositionClass = isRTL ? 'right-[162px]' : 'left-[162px]';
  const actionButtonClass = 'fixed top-5 z-[60] flex size-7 items-center justify-center rounded-md p-0 transition-colors hover:bg-white/[0.04]';

  useEffect(() => {
    setHasMounted(true);
  }, []);

  if (!hasMounted) {
    return null;
  }

  const renderIcon = () => {
    return (
      <Image
        src="/images/sidebar/collapse.svg"
        alt=""
        width={28}
        height={28}
        data-testid="sidebar-toggle-icon"
        className={cn('size-7', isRTL && 'scale-x-[-1]')}
        aria-hidden="true"
      />
    );
  };

  return (
    <>
      <button
        onClick={isOpen ? closeSidebar : openSidebar}
        className={cn(actionButtonClass, togglePositionClass, 'cursor-pointer')}
        aria-label={label}
        title={label}>
        {renderIcon()}
      </button>

      {!isOpen && (
        <button
          type="button"
          className={cn(
            actionButtonClass,
            newChatPositionClass,
            'hidden md:flex',
            newChat.isDisabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
          )}
          onClick={() => newChat.startNewChat()}
          disabled={newChat.isDisabled}
          aria-label={t('sidebar.newChat')}
          title={newChat.buttonTitle ?? t('sidebar.newChat')}>
          <Plus className="size-7 text-[#cccccc]" />
        </button>
      )}

      <SessionAccessDialog
        open={newChat.limitDialog.open}
        onOpenChange={newChat.setLimitDialogOpen}
        variant="limit"
        language={language}
        level={user?.level ?? 'L0'}
        limit={newChat.limitDialog.limit}
      />
    </>
  );
}
